import { forwardRef, useMemo } from "react";

import Box, { BoxProps } from "@mui/material/Box";
import { SxProps, Theme } from "@mui/material/styles";
import { useTheme } from "@mui/styles";

import { useInteractiveElementEvents } from "../../../hooks/useInteractiveElementEvents";
import { LegacyZeffyCorePalette, IconSize, ZeffyColorTokenText, ZeffyPaletteIntensity } from "../../../types/mui";
import { ZeffyColorTokenTextClasses } from "../helpers";

import { mergeSx } from "@/helpers/mergeSx";
import { IconElement } from "@/icons/types";

export type DashboardIconProps = Omit<BoxProps, "classes" | "children"> & {
  vibe?: LegacyZeffyCorePalette | ZeffyColorTokenTextClasses | "inherit" | "currentColor";
  size?: IconSize;
  children: IconElement;
  sx?: SxProps<Theme>;
  inline?: boolean;
};

export const DashboardIcon = forwardRef<HTMLDivElement, DashboardIconProps>(
  ({ vibe = "tertiary", size = "medium", inline = false, children, onKeyDown, onKeyUp, onClick, sx, ...rest }, ref) => {
    const theme = useTheme();
    const { handleKeyDown, handleKeyUp } = useInteractiveElementEvents<HTMLDivElement>({ onClick, onKeyDown, onKeyUp });

    const iconSize = inline ? theme.icon.size.inline[size] : theme.icon.size.default[size];

    const vibeColor = useMemo(() => {
      if (vibe === "inherit" || vibe === "currentColor") {
        return undefined;
      }

      if (vibe in theme.palette && "main" in theme.palette[vibe as LegacyZeffyCorePalette]) {
        // Legacy token detected
        return theme.palette[vibe as LegacyZeffyCorePalette].main;
      }

      if (vibe.startsWith("text-")) {
        const typedVibe = vibe as ZeffyColorTokenTextClasses;
        const [, palette, intensity] = typedVibe.split("-");

        if (typeof theme.palette.text[palette as keyof ZeffyColorTokenText] === "string") {
          // Legacy token detected
          return theme.palette.text[palette as keyof ZeffyColorTokenText];
        }

        const typedPalette = palette as keyof ZeffyColorTokenText;
        const typedIntensity = intensity as keyof ZeffyPaletteIntensity;

        if (!typedPalette || !typedIntensity) {
          throw new Error(`Invalid vibe: ${vibe}`);
        }

        const paletteValue = theme.palette.text[typedPalette] as ZeffyPaletteIntensity | undefined;
        const intensityValue = paletteValue?.[typedIntensity] as string | undefined;

        if (!intensityValue) {
          throw new Error(`Invalid vibe: ${vibe}`);
        }

        return intensityValue;
      }

      throw new Error(`Invalid vibe: ${vibe}`);
    }, [vibe, theme]);

    const styles = mergeSx(
      {
        fill: vibe === "inherit" ? "inherit" : "currentColor",
        color: vibeColor,
        width: iconSize,
        height: iconSize,
        lineHeight: `${iconSize}px`,
        fontSize: iconSize,
        ["& > svg"]: {
          width: "100%",
          height: "100%",
        },
      },
      sx
    );

    return (
      <Box ref={ref} sx={styles} {...rest} onKeyDown={handleKeyDown} onKeyUp={handleKeyUp} onClick={onClick}>
        {children}
      </Box>
    );
  }
);

DashboardIcon.displayName = "DashboardIcon";
